/**
 * sticky-header — moved out of sections/header.liquid {% javascript %}.
 *
 * Shopify compiled that block to compiled_assets/scripts.js, which left the
 * mobile browser document-load indicator spinning. This asset loads with defer,
 * initializes after window "load", and does not cloneNode the header section
 * (cloning during load re-queued resources on mobile).
 */
(function () {
  if (customElements.get('sticky-header')) return;

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (this._acBound) return;
      this._acBound = true;

      const start = () => {
        try {
          this.init();
        } catch (e) {
          console.error(e);
        }
      };

      if (document.readyState === 'complete') {
        window.setTimeout(start, 0);
      } else {
        window.addEventListener('load', start, { once: true });
      }
    }

    init() {
      this.header = document.querySelector('.section-header');
      if (!this.header) return;

      this.headerIsAlwaysSticky =
        this.getAttribute('data-sticky-type') === 'always' ||
        this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};

      this.cacheHeaderHeights();

      window.matchMedia('(max-width: 990px)').addEventListener('change', () => this.cacheHeaderHeights());
      window.addEventListener('resize', () => {
        window.clearTimeout(this.headerResizeTimer);
        this.headerResizeTimer = setTimeout(() => this.cacheHeaderHeights(), 150);
      });

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      }

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => {
        this.preventReveal = true;
      };

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });

      this.createObserver();
    }

    /** Live measure only — never cloneNode the section during/after load. */
    cacheHeaderHeights() {
      if (!this.header) return;
      this.setHeaderHeight();
      const h = this.header.offsetHeight;
      if (!this.header.classList.contains('header-compact')) {
        document.documentElement.style.setProperty('--header-expanded-height', `${h}px`);
        const compactGuess = Math.max(48, Math.round(h * 0.72));
        if (!document.documentElement.style.getPropertyValue('--header-compact-height')) {
          document.documentElement.style.setProperty('--header-compact-height', `${compactGuess}px`);
        }
      }
    }

    setHeaderHeight() {
      if (!this.header) return;
      const h = this.header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${h}px`);
      if (this.header.classList.contains('header-compact')) {
        document.documentElement.style.setProperty('--header-compact-height', `${h}px`);
      } else {
        document.documentElement.style.setProperty('--header-expanded-height', `${h}px`);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      if (this.onScrollHandler) {
        window.removeEventListener('scroll', this.onScrollHandler);
      }
    }

    createObserver() {
      if (!this.header || typeof IntersectionObserver === 'undefined') return;
      const observer = new IntersectionObserver((entries, obs) => {
        this.headerBounds = entries[0].intersectionRect;
        obs.disconnect();
      });
      observer.observe(this.header);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

      if (this.headerIsAlwaysSticky) {
        this.header.classList.toggle('scrolled-past-header', scrollTop > 12);
        /* Mobile: never compact — stable icon layout / no shrink on scroll */
        if (window.matchMedia('(max-width: 989px)').matches) {
          if (this.header.classList.contains('header-compact')) {
            this.header.classList.remove('header-compact');
            document.documentElement.classList.remove('header-compact');
            const expanded = getComputedStyle(document.documentElement)
              .getPropertyValue('--header-expanded-height')
              .trim();
            if (expanded) {
              document.documentElement.style.setProperty('--header-height', expanded);
            } else {
              this.setHeaderHeight();
            }
          }
          this.currentScrollTop = scrollTop;
          return;
        }
        const wasCompact = this.header.classList.contains('header-compact');
        const COMPACT_ENTER = 100;
        const COMPACT_EXIT = 40;
        const isCompact = wasCompact ? scrollTop > COMPACT_EXIT : scrollTop > COMPACT_ENTER;
        this.header.classList.toggle('header-compact', isCompact);
        document.documentElement.classList.toggle('header-compact', isCompact);
        if (wasCompact !== isCompact) {
          const key = isCompact ? '--header-compact-height' : '--header-expanded-height';
          const known = getComputedStyle(document.documentElement).getPropertyValue(key).trim();
          if (known) {
            document.documentElement.style.setProperty('--header-height', known);
          }
          window.clearTimeout(this.compactHeightTimer);
          this.compactHeightTimer = setTimeout(() => this.setHeaderHeight(), 400);
        }
        this.currentScrollTop = scrollTop;
        return;
      }

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);
          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);
          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach((disclosure) => {
        if (disclosure && typeof disclosure.close === 'function') disclosure.close();
      });
    }

    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      if (this.searchModal && typeof this.searchModal.close === 'function') {
        this.searchModal.close(false);
      }
    }
  }

  customElements.define('sticky-header', StickyHeader);
})();
